import * as ClipperLib from "clipper-lib";
import { unionPolygons } from "../unionPolygons";
import type { PolygonRing } from "../types";
import type { ClosePolygonGaps } from "./types";

// Clipper works on integers; this keeps a hundredth of a PDF point.
const SCALE = 1000;
const MITER_LIMIT = 2;
const ARC_TOLERANCE = 0.25 * SCALE;

const toPath = (ring: PolygonRing): ClipperLib.Path =>
  ring.map((point) => ({
    X: Math.round(point.x * SCALE),
    Y: Math.round(point.y * SCALE),
  }));

const fromPath = (path: ClipperLib.Path): PolygonRing =>
  path.map((point) => ({ x: point.X / SCALE, y: point.Y / SCALE }));

const offsetPaths = (
  paths: ClipperLib.Paths,
  delta: number,
): ClipperLib.Paths => {
  const offset = new ClipperLib.ClipperOffset(MITER_LIMIT, ARC_TOLERANCE);
  offset.AddPaths(
    paths,
    ClipperLib.JoinType.jtMiter,
    ClipperLib.EndType.etClosedPolygon,
  );
  const solution: ClipperLib.Paths = [];
  offset.Execute(solution, delta * SCALE);
  return solution;
};

// Morphological closing: grow, which fuses pieces across small gaps, then shrink back.
export const closePolygonGaps: ClosePolygonGaps = ({ pieces, radius }) => {
  const paths: ClipperLib.Paths = [];
  for (const piece of pieces) {
    for (const ring of piece) paths.push(toPath(ring));
  }
  const closed = offsetPaths(offsetPaths(paths, radius), -radius);
  // Only the outer rings are kept: a courtyard enclosed by the pieces becomes part of them.
  const outerRings = closed
    .filter((path) => ClipperLib.Clipper.Orientation(path))
    .map((path) => [fromPath(path)]);
  // The originals go back in so the integer rounding never eats their area.
  return unionPolygons({ pieces: [...outerRings, ...pieces] });
};
